import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { postWithRetry } from "../lib/apiResilient";
import { ArrowLeft, Calendar, Sparkles } from "lucide-react";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";
import { Reveal, MaskReveal } from "../components/Reveal";

const API = process.env.REACT_APP_BACKEND_URL;

const empty = {
  name: "", email: "", phone: "",
  instagram: "", other_socials: "", website: "",
  top_questions: "", comments: "",
};

export default function StudioCoachingApply() {
  const [form, setForm] = useState(empty);
  const [terms, setTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.top_questions) {
      toast.error("Name, email, and your top questions are required.");
      return;
    }
    if (!terms) {
      toast.error("Please accept the no-refunds terms.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await postWithRetry(`${API}/api/studio/coaching-apply`, {
        ...form,
        origin_url: window.location.origin,
        terms_accepted: true,
      }, { timeout: 18000 });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream text-ink pt-24 md:pt-32 pb-32" data-testid="coaching-apply-page">
      <Toaster richColors position="top-right"/>
      <div className="max-w-screen-md mx-auto px-6">
        <Link to="/studio/coaching-call" className="overline text-ink/55 inline-flex items-center gap-2 hover:text-ink mb-8" data-testid="coaching-back">
          <ArrowLeft size={12}/> Back to overview
        </Link>

        <div className="overline text-ink/55 flex items-center gap-2">
          <Sparkles size={12}/> Edition C01 · Private Council · Limited to 10 founders this quarter
        </div>
        <MaskReveal>
          <h1 className="font-display uppercase text-5xl sm:text-7xl leading-[0.9] mt-3 mb-6">
            The Founder Hour<br/>
            <span className="serif-italic font-serif normal-case">+ every edition we&apos;ve ever published</span>.
          </h1>
        </MaskReveal>

        {/* ───── Value stack ───── */}
        <div className="bg-ink text-cream p-6 sm:p-8 mb-10 max-w-2xl" data-testid="coaching-value-stack">
          <div className="overline text-cream/55 mb-4">What&apos;s included</div>
          <ul className="space-y-3 text-sm sm:text-base">
            <li className="flex justify-between gap-4">
              <span>One 60-minute private session with Meghan</span>
              <span className="font-mono tabular-nums text-cream/70">$1,000</span>
            </li>
            <li className="flex justify-between gap-4">
              <span>Every paid Playbook · 21 editions ($59 each)</span>
              <span className="font-mono tabular-nums text-cream/70">$1,239</span>
            </li>
            <li className="flex justify-between gap-4">
              <span>Every Mini-Course &amp; Audio Series · 8 editions</span>
              <span className="font-mono tabular-nums text-cream/70">$312</span>
            </li>
            <li className="flex justify-between gap-4">
              <span>Every Ebook · 2 editions</span>
              <span className="font-mono tabular-nums text-cream/70">$98</span>
            </li>
            <li className="flex justify-between gap-4">
              <span>Every Workbook + Guide · 3 editions</span>
              <span className="font-mono tabular-nums text-cream/70">$73</span>
            </li>
            <li className="flex justify-between gap-4 pt-3 border-t border-cream/15">
              <span className="font-medium">Total stack value</span>
              <span className="font-display text-2xl">$2,722</span>
            </li>
            <li className="flex justify-between gap-4 items-baseline">
              <span className="overline text-cream/55">Your founder rate</span>
              <span className="font-display text-4xl">$997</span>
            </li>
            <li className="flex justify-between gap-4 items-baseline">
              <span className="overline text-cream/55">You save</span>
              <span className="font-serif italic text-lg">$1,725 · 63% off</span>
            </li>
          </ul>
        </div>

        <Reveal delay={0.2}>
          <p className="text-ink/80 text-lg leading-relaxed mb-3 max-w-2xl">
            The Founder Hour is the single fastest path into the M Media universe. You pay once, get one hour of my unfiltered eyes on your business, <strong>and</strong> the entire library — every Playbook, every Ebook, every Audio Series — unlocks in your private shelf the moment payment clears.
          </p>
          <p className="font-serif italic text-ink/75 text-base mb-12 max-w-2xl">
            Limited to 10 founders per quarter. Once 10 calls are booked, the rate goes back to $1,997 and the library bundle is removed.
          </p>
        </Reveal>

        <p className="text-ink/75 text-sm leading-relaxed mb-12 max-w-2xl">
          Once payment clears, the desk reviews your intake personally. Within <strong>5 business days</strong> you&apos;ll receive a Calendly invitation to book your 60-minute session — schedulable within 30 days of booking. Library access is instant.
        </p>

        <form onSubmit={submit} className="space-y-8" data-testid="coaching-form">
          <Field label="Full name *">
            <input required value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="coaching-name"
              className="w-full border-b border-ink/30 bg-transparent py-2 outline-none focus:border-ink text-ink"/>
          </Field>
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Email *">
              <input required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="coaching-email"
                className="w-full border-b border-ink/30 bg-transparent py-2 outline-none focus:border-ink text-ink"/>
            </Field>
            <Field label="Phone (optional)">
              <input value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="coaching-phone"
                className="w-full border-b border-ink/30 bg-transparent py-2 outline-none focus:border-ink text-ink"/>
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Instagram handle">
              <input placeholder="@yourbrand" value={form.instagram} onChange={(e) => update("instagram", e.target.value)} data-testid="coaching-instagram"
                className="w-full border-b border-ink/30 bg-transparent py-2 outline-none focus:border-ink text-ink"/>
            </Field>
            <Field label="Website">
              <input placeholder="https://…" value={form.website} onChange={(e) => update("website", e.target.value)} data-testid="coaching-website"
                className="w-full border-b border-ink/30 bg-transparent py-2 outline-none focus:border-ink text-ink"/>
            </Field>
          </div>

          <Field label="Other social handles (TikTok, LinkedIn, X, etc.)">
            <input value={form.other_socials} onChange={(e) => update("other_socials", e.target.value)} data-testid="coaching-other-socials"
              className="w-full border-b border-ink/30 bg-transparent py-2 outline-none focus:border-ink text-ink"/>
          </Field>

          <Field label="Top questions you want answered on the call *">
            <textarea required rows={5} value={form.top_questions} onChange={(e) => update("top_questions", e.target.value)} data-testid="coaching-questions"
              placeholder="Be specific. The more we know, the further we can take you in 60 minutes."
              className="w-full border-b border-ink/30 bg-transparent py-2 outline-none focus:border-ink text-ink resize-none"/>
          </Field>

          <Field label="Anything else we should know (optional)">
            <textarea rows={3} value={form.comments} onChange={(e) => update("comments", e.target.value)} data-testid="coaching-comments"
              className="w-full border-b border-ink/30 bg-transparent py-2 outline-none focus:border-ink text-ink resize-none"/>
          </Field>

          <div className="border-t border-ink/15 pt-8">
            <label className="flex items-start gap-3 text-sm text-ink/80 cursor-pointer" data-testid="coaching-terms-row">
              <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-1" data-testid="coaching-terms"/>
              <span>I understand all sales are final — <strong>no refunds, no exchanges.</strong> If I fail to book within 30 days the session is forfeited.</span>
            </label>
          </div>

          <div className="bg-ink/5 border border-ink/15 p-6 flex items-start gap-4 text-sm">
            <Calendar size={20} className="text-ink/60 shrink-0 mt-0.5"/>
            <div>
              <p className="font-medium text-ink">What happens next</p>
              <ol className="mt-2 space-y-1 text-ink/70 list-decimal list-inside">
                <li>Pay $997 via Stripe (next screen).</li>
                <li>The desk reviews your intake personally.</li>
                <li>Within 5 business days you receive a Calendly invite.</li>
                <li>Book your 60-minute session within 30 days.</li>
              </ol>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting || !terms}
            data-testid="coaching-submit"
            className={`w-full py-5 text-xs tracking-[0.25em] uppercase font-medium transition-colors ${
              submitting || !terms ? "bg-ink/30 text-cream cursor-not-allowed" : "bg-ink text-cream hover:bg-ink/90"
            }`}
          >
            {submitting ? "Opening Stripe…" : "Continue to payment · $997"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="overline text-ink/60">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
